import clsx from "clsx";
import { memo, useEffect, useRef } from "react";
import noImage from "@/assets/img/no-image.svg";
import { useArchiveItemPresenter } from "@/features/archive/components/useArchiveItemPresenter";
import type { ArchiveEventNotifier } from "@/features/archive/mediators/archiveEvents";
import type { ArchiveThread } from "@/features/archive/types";
import { CatalogBoardIcon } from "@/features/catalog/components/CatalogBoardIcon";
import { CatalogThreadLink } from "@/features/catalog/components/navigation/CatalogThreadLink";
import { ComponentScope } from "@/shared/ui/component-hierarchy/ComponentScope";

type ArchiveItemProps = {
  readonly thread: ArchiveThread;
  readonly openInNewTab?: boolean;
};

export type ArchiveItemViewProps = {
  readonly archivedAtLabel: string;
  readonly imageUrl: string;
  readonly isR18Hidden: boolean;
  readonly isR18Revealed: boolean;
  readonly linkAttributes: React.AnchorHTMLAttributes<HTMLAnchorElement>;
  readonly onEvent: ArchiveEventNotifier;
  readonly textLines: number;
  readonly thread: ArchiveThread;
  readonly title: string;
};

/** モバイル版過去ログセルのPresenter。 */
export const ArchiveItem: React.FunctionComponent<ArchiveItemProps> = memo(
  function ArchiveItem({ thread, openInNewTab }: ArchiveItemProps) {
    const presentation = useArchiveItemPresenter(thread, openInNewTab);
    const { node, ...viewProps } = presentation;

    return (
      <ComponentScope node={node}>
        <ArchiveItemView thread={thread} {...viewProps} />
      </ComponentScope>
    );
  },
);

/** propsから描画し、操作をtyped eventとして通知するモバイルView。 */
export const ArchiveItemView: React.FunctionComponent<ArchiveItemViewProps> = ({
  archivedAtLabel,
  imageUrl,
  isR18Hidden,
  isR18Revealed,
  linkAttributes,
  onEvent,
  textLines,
  thread,
  title,
}: ArchiveItemViewProps) => {
  const linkRef = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    if (isR18Revealed) linkRef.current?.focus();
  }, [isR18Revealed]);

  return (
    <article
      aria-label={title}
      className="group relative bg-card/50 rounded-lg overflow-hidden hover:bg-card transition-colors"
    >
      {isR18Hidden && (
        <button
          type="button"
          className="archive-r18-spoiler-trigger"
          aria-label={`${title}のR18画像を表示`}
          onClick={(): void => {
            onEvent({ type: "archive/item-reveal-requested" });
          }}
        />
      )}
      <CatalogThreadLink
        ref={linkRef}
        thread={{ id: thread.threadId, b: thread.b }}
        {...linkAttributes}
        className="block focus:outline-none focus:ring-2 focus:ring-primary focus:ring-inset"
      >
        <figure className="relative aspect-square bg-muted flex items-center justify-center">
          <img
            src={imageUrl}
            alt={title}
            loading="lazy"
            className={clsx(
              "w-full h-full object-contain",
              isR18Hidden && "r18-spoiler-image",
            )}
            onError={(event: React.SyntheticEvent<HTMLImageElement>): void => {
              event.currentTarget.src = noImage;
            }}
          />
          <data
            value={thread.replyCount}
            className="absolute bottom-1 right-1 px-1.5 py-0.5 text-xs font-bold bg-black/70 text-white rounded"
          >
            {thread.replyCount}レス
          </data>
        </figure>
        <header
          className={clsx(
            "flex flex-col px-2",
            textLines > 0 ? "gap-1 py-2" : "py-1",
          )}
        >
          {textLines > 0 && (
            <p
              className="catalog-text-clamp min-w-0 text-xs text-muted-foreground leading-tight transition-colors group-hover:text-foreground"
              style={
                {
                  "--catalog-text-lines": textLines,
                } as React.CSSProperties
              }
            >
              {title}
            </p>
          )}
          <time
            dateTime={thread.archivedAt}
            className="archive-saved-at text-muted-foreground truncate"
          >
            {archivedAtLabel}
          </time>
          <CatalogBoardIcon slug={thread.b} />
        </header>
      </CatalogThreadLink>
    </article>
  );
};
